import React from 'react';
import classNames from 'classnames';

type Level = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface StepIndicatorProps {
  headerAs?: Level;
  className?: string;
  id?: string;
  labels: string[];
  stepIndex: number;
  hideLabels?: boolean;
  centerLabels?: boolean;
  counters?: 'small' | 'large';
}

const StepIndicator = ({
  className,
  id,
  labels,
  hideLabels,
  centerLabels,
  counters,
  stepIndex,
  headerAs: Component = 'h2',
}: StepIndicatorProps): JSX.Element => {
  const currentStep = stepIndex + 1;
  const totalSteps = labels.length;

  const stepIndicatorClassName = classNames(
    'usa-step-indicator',
    {
      'usa-step-indicator--center': centerLabels,
      'usa-step-indicator--counters-sm': counters === 'small',
      'usa-step-indicator--counters': counters === 'large',
      'usa-step-indicator--no-labels': hideLabels,
    },
    className,
  );

  const stepListItems = labels.map((label, index) => {
    return (
      <li
        className={classNames('usa-step-indicator__segment', {
          'usa-step-indicator__segment--complete': index < stepIndex,
          'usa-step-indicator__segment--current': index === stepIndex,
        })}
        aria-current={index === stepIndex && !hideLabels ? 'true' : undefined}
        key={label}
      >
        <span className="usa-step-indicator__segment-label">
          {label}{' '}
          {index !== stepIndex ? (
            <span className="usa-sr-only">{index < stepIndex ? 'completed' : 'not completed'}</span>
          ) : null}
        </span>
      </li>
    );
  });

  const activeLabel = labels[stepIndex];
  return (
    <div id={id} className={stepIndicatorClassName} aria-label="progress" role="progressbar">
      <ol className="usa-step-indicator__segments">{stepListItems}</ol>
      <div className="usa-step-indicator__header">
        <Component className="usa-step-indicator__heading">
          <span className="usa-step-indicator__heading-counter">
            <span className="usa-sr-only">Step</span>{' '}
            <span className="usa-step-indicator__current-step">{currentStep}</span>{' '}
            <span className="usa-step-indicator__total-steps">of {totalSteps}</span>
          </span>
          <span className="usa-step-indicator__heading-text">{` ${activeLabel}`}</span>
        </Component>
      </div>
    </div>
  );
};

export default StepIndicator;
